import React from 'react';
import styled from '@emotion/styled';
import { Text, TextLabel } from 'ds/components/typography';
import { useLocale } from 'locale';
import { UserPromotionPlan, UserBulletinPromotionPlan, PromotionPlanVariant } from 'store/sagas/apiService/types';
import { planEnhancer } from './helpers';
import { head, pick } from 'lodash';
import { PromotionPlanFeature } from './modals/private-bulletin-select-promotion-plan-modal/types';
import CrossGreyIcon from 'assets/svg/cross-grey.svg';
import { BulletinStatusType } from 'utils/entities';

const PromotionPlanInfoSectionRoot = styled.div(() => ({
  width: '100%',
}));

const PlanInfoSection = styled.div(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
}));

const InfoRow = styled.div<{ hasIcon?: boolean }>(({ theme, hasIcon }) => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(hasIcon ? 1.5 : 0.75),
}));


const FeaturesGroup = styled.div(({ theme }) => ({
  display: 'flex',
  marginTop: theme.spacing(3),
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

export interface PromotionPlanInfoSectionProps {
  promotionPlan: UserPromotionPlan | UserBulletinPromotionPlan;
  rootStyle?: React.CSSProperties;
  status: BulletinStatusType | null;
}

const PromotionPlanInfoSection: React.FC<PromotionPlanInfoSectionProps> = ({ promotionPlan, rootStyle, status }) => {
  const { t, formatMoney, formatDate } = useLocale();
  const { features }: { features: PromotionPlanFeature[] } = pick(head(planEnhancer([ promotionPlan.promotionPlan ], t)), 'features');
  const nextChargeDate = 'nextChargeDate' in promotionPlan ? promotionPlan.nextChargeDate : null;

  return (
    <PromotionPlanInfoSectionRoot style={rootStyle}>
      <PlanInfoSection>
        <InfoRow>
          <TextLabel weight="bold">{t('cancelBulletinSubscriptionPromotionPlanModal.planTitle')}</TextLabel>
          <Text>{promotionPlan.promotionPlan.name}</Text>
        </InfoRow>
        {promotionPlan.promotionPlan.variant === PromotionPlanVariant.SUBSCRIPTION_MONTHLY ? (
          <InfoRow>
            <TextLabel weight="bold">{t('cancelBulletinSubscriptionPromotionPlanModal.amountToCharge')}</TextLabel>
            <Text>{formatMoney(promotionPlan.promotionPlan.price)}</Text>
          </InfoRow>
        ) : null}
        {nextChargeDate ? (
          <InfoRow>
            <TextLabel weight="bold">{t('manageBulletinPage.promotionPlan.nextChargeDate')}</TextLabel>
            <Text>{formatDate(nextChargeDate)}</Text>
          </InfoRow>
        ) : null}
        {features && features.length ? (
          <FeaturesGroup>
            <TextLabel weight="bold">{t('manageBulletinPage.bulletin.features', { status })}</TextLabel>
            {features.map(feature => (
              <InfoRow hasIcon key={feature.id}>
                <CrossGreyIcon />
                <Text>{feature.text}</Text>
              </InfoRow>
            ))}
          </FeaturesGroup>
        ) : null}
      </PlanInfoSection>
    </PromotionPlanInfoSectionRoot>
  );
};

export default PromotionPlanInfoSection;
